import { type InclusionExclusionListItem, type InclusionExclusionListQuery } from '@utazzo/shared';
import { UzBadge } from '@utazzo/ui';
import React from 'react';
import { useSearchParams } from 'react-router-dom';

import { InclusionExclusionFormDrawer } from '../components/InclusionExclusionFormDrawer';
import {
  useDeleteInclusionExclusion,
  useInclusionsExclusionsList,
} from '../use-inclusions-exclusions';

import { type DataGridColumn, type DataGridFilter } from '@/components/data-grid';
import { ListScreen, type ListScreenColumns } from '@/components/list-screen';
import { useCurrentUser } from '@/features/auth/use-auth';

type IEType = 'inclusion' | 'exclusion';

const TYPE_OPTIONS = [
  { value: '', label: 'All types' },
  { value: 'inclusion', label: 'Inclusions' },
  { value: 'exclusion', label: 'Exclusions' },
] as const;

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
] as const;

const MIN_SEARCH_LENGTH = 3;

const FILTERS: ReadonlyArray<DataGridFilter> = [
  {
    id: 'type',
    label: 'Type',
    options: TYPE_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-44',
  },
  {
    id: 'isActive',
    label: 'Status',
    options: STATUS_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-44',
  },
];

export const InclusionsExclusionsListPage = (): React.JSX.Element => {
  const me = useCurrentUser();
  const canDelete = me?.role === 'ADMIN' || me?.role === 'OPS';
  const deleteEntry = useDeleteInclusionExclusion();

  // Seed the create form's type from the active list filter (`?f.type=`).
  const [searchParams] = useSearchParams();
  const activeType = searchParams.get('f.type');
  const defaultType: IEType = activeType === 'exclusion' ? 'exclusion' : 'inclusion';

  const columns: ListScreenColumns<InclusionExclusionListItem> = ({ state }) => {
    const cols: ReadonlyArray<DataGridColumn<InclusionExclusionListItem>> = [
      {
        id: 'serial',
        header: 'S.No',
        width: '64px',
        cell: (_row, index) => (
          <span className="text-zinc-500">{(state.page - 1) * state.pageSize + index + 1}.</span>
        ),
      },
      {
        id: 'type',
        header: 'Type',
        cell: (row) => <UzBadge tone="secondary">{row.type}</UzBadge>,
      },
      {
        id: 'title',
        header: 'Title',
        sortable: true,
        cell: (row) => <span className="font-medium text-zinc-900">{row.title}</span>,
      },
      {
        id: 'isActive',
        header: 'Status',
        cell: (row) => (
          <UzBadge tone={row.isActive ? 'success' : 'secondary'}>
            {row.isActive ? 'Active' : 'Inactive'}
          </UzBadge>
        ),
      },
    ];
    return cols;
  };

  return (
    <ListScreen<InclusionExclusionListItem, InclusionExclusionListQuery>
      title="Inclusions & Exclusions"
      description="Reusable catalogue of standard inclusions and exclusions."
      useList={useInclusionsExclusionsList}
      defaultSort={{ by: 'title', dir: 'asc' }}
      minSearchLength={MIN_SEARCH_LENGTH}
      toQuery={(state, base) => {
        const type = state.filters.type;
        const isActive = state.filters.isActive;
        return {
          ...base,
          ...(type ? { type } : {}),
          ...(isActive ? { isActive: isActive === 'true' } : {}),
        };
      }}
      columns={columns}
      filters={FILTERS}
      getRowId={(row) => row.id}
      createButton={{ label: 'Add entry' }}
      renderDrawer={({ open, mode, row, close }) => (
        <InclusionExclusionFormDrawer
          open={open}
          onOpenChange={(o) => {
            if (!o) close();
          }}
          mode={mode}
          entry={row}
          defaultType={defaultType}
        />
      )}
      rowActions={{
        edit: true,
        delete: {
          onDelete: (row) => deleteEntry.mutateAsync(row.id),
          disabled: () => !canDelete,
          confirm: (row) => ({
            title: 'Delete this entry?',
            description: `${row.title} will be soft-deleted.`,
          }),
        },
      }}
      copy={{
        searchPlaceholder: 'Search by title (min 3 characters)',
        emptyTitle: 'No inclusions or exclusions match these filters',
        emptyDescription: 'Adjust the type/status filter or clear the search box.',
        errorTitle: 'Failed to load inclusions & exclusions',
        errorDescription: 'The server returned an error. Try refreshing or check your network.',
      }}
    />
  );
};
